import { useRef, useState } from "react";
import { eventById, rulesFor, scoreProblem } from "../../../../engine/scoring";
import type { GameRecord, Round } from "../../../../engine/types";
import { ConfirmButton, CourtBadge, Stepper, Toggle, clockTime, eventName, pairNames, scoreText, type TabProps } from "./common";
import { Fold } from "./Fold";
import { actor } from "./words";

function GameEditor({ t, state, round, game }: TabProps & { round: Round; game: GameRecord }) {
  const [a, setA] = useState(game.score?.a ?? 0);
  const [b, setB] = useState(game.score?.b ?? 0);
  const [capped, setCapped] = useState(game.score?.capped ?? false);
  const [reason, setReason] = useState(game.score ? "" : "Entered by the admin");
  const [voidReason, setVoidReason] = useState("");
  const problem = scoreProblem({ a, b, capped }, rulesFor(state.config, eventById(state.config, game.eventId)));
  const where = { roundNo: round.no, court: game.court };

  return (
    <div className="ad-editor">
      {game.history.length > 0 && (
        <>
          <h3>History</h3>
          <ul className="ad-history">
            {game.history.map((h, i) => (
              <li key={i}>
                {clockTime(h.at)} {scoreText(h.score)} by {actor(state, h.by)}{h.reason ? `: ${h.reason}` : ""}
              </li>
            ))}
          </ul>
        </>
      )}
      {game.voided ? (
        <p className="ad-note">Voided: {game.voidReason ?? "no reason given"}</p>
      ) : (
        <>
          <h3>{game.score ? "Correct the score" : "Enter the score"}</h3>
          {/* nothing in the log makes a game owed again, so a first score has no Undo */}
          {!game.score && <p className="ad-note">No Undo for a first score: Correct the score puts it right.</p>}
          <div className="ad-grid2">
            <Stepper label={pairNames(state, game.a)} value={a} onChange={setA} />
            <Stepper label={pairNames(state, game.b)} value={b} onChange={setB} />
          </div>
          <Toggle label="Time ran out" on={capped} onChange={setCapped} />
          <label className="ad-field">
            <span>Reason</span>
            <input value={reason} placeholder="Why it changed" onChange={(e) => setReason(e.target.value)} />
          </label>
          {problem && <p className="ad-note ad-owed">{problem}</p>}
          <button type="button" className="btn ad-wide" disabled={problem !== null || !reason.trim()}
            onClick={() => t.send({ type: "game.corrected", ...where, score: { a, b, capped }, reason: reason.trim() })}>
            Save {a}-{b}
          </button>
          <h3>Void this game</h3>
          <input value={voidReason} placeholder="Reason" aria-label="Reason to void" onChange={(e) => setVoidReason(e.target.value)} />
          <ConfirmButton label="Void" className="btn warn ad-wide" yes="Void it" disabled={!voidReason.trim()}
            question={`Void round ${round.no}, court ${game.court}: ${pairNames(state, game.a)} v ${pairNames(state, game.b)}? This cannot be taken back.`}
            onConfirm={() => t.send({ type: "game.voided", ...where, reason: voidReason.trim() })} />
        </>
      )}
    </div>
  );
}

const COUNT_WORDS: Record<number, string> = { 1: "the one", 2: "both", 3: "all three", 4: "all four" };

/** A round in a few words, for its closed line: "all four in", "3 in, 1 void", "2 owed". */
function roundLine(round: Round, playing: boolean): { text: string; owed: boolean } {
  const n = round.games.length;
  const voided = round.games.filter((g) => g.voided).length;
  const scored = round.games.filter((g) => !g.voided && g.score).length;
  const owed = n - voided - scored;
  if (playing) return { text: `being played, ${scored + voided} of ${n} in`, owed: false };
  if (owed > 0) return { text: `${owed} owed`, owed: true };
  if (voided > 0) return { text: scored ? `${scored} in, ${voided} void` : `${voided} void`, owed: false };
  return { text: `${COUNT_WORDS[n] ?? `all ${n}`} in`, owed: false };
}

/**
 * Every round, newest first. The round being played and any round with a score owed are open;
 * a round with every score in is one closed line that opens on a tap, so a wrong score from an
 * old round is one tap away. A round that was open stays open while this tab is on show: nothing
 * closes under a finger when a late score lands.
 */
export function ScoresTab(props: TabProps) {
  const { state } = props;
  const [open, setOpen] = useState<string | null>(null);
  const [asked, setAsked] = useState<Record<number, boolean>>({});
  const stayed = useRef(new Set<number>());
  const rounds = [...state.rounds].reverse();
  const playingNo = state.clock.phase === "playing" ? state.clock.roundNo : null;

  if (!rounds.length) {
    return (
      <div className="ad-tab">
        <section className="ad-card ad-flat"><p className="ad-note">No rounds played yet.</p></section>
      </div>
    );
  }

  return (
    <div className="ad-tab ad-scores">
      {rounds.map((round) => {
        const line = roundLine(round, round.no === playingNo);
        if (round.no === playingNo || line.owed) stayed.current.add(round.no);
        const isOpen = asked[round.no] ?? stayed.current.has(round.no);
        return (
          <Fold key={round.no} title={`Round ${round.no}`} summary={line.text} warn={line.owed} open={isOpen}
            onToggle={(next) => setAsked((was) => ({ ...was, [round.no]: next }))}>
            <p className="ad-note">
              {round.mode === "mexicano" ? "Mexicano" : "Americano"}, {clockTime(round.startedAt)}
              {round.no === playingNo && <span className="ad-tag ok">Being played now</span>}
            </p>
            <ul className="ad-list">
              {round.games.map((g) => {
                const key = `${round.no}:${g.court}`;
                const gameOpen = open === key;
                return (
                  <li key={key} className={"ad-scoreitem" + (gameOpen ? " open" : "")}>
                    <button type="button" className="ad-game ad-game-btn" aria-expanded={gameOpen} onClick={() => setOpen(gameOpen ? null : key)}>
                      <CourtBadge court={g.court} />
                      <span className="ad-game-body">
                        <span>{pairNames(state, g.a)}</span>
                        <span><span className="ad-v">v</span> {pairNames(state, g.b)}</span>
                        {g.eventId && <span className="ad-event">{eventName(state, g.eventId)}</span>}
                      </span>
                      <span className={"ad-score" + (!g.voided && !g.score && round.no !== playingNo ? " ad-owed" : "")}>
                        {g.voided ? "void" : g.score ? scoreText(g.score) : round.no === playingNo ? "playing" : "owed"}
                      </span>
                    </button>
                    {gameOpen && <GameEditor key={`${key}:${g.history.length}:${g.voided}`} {...props} round={round} game={g} />}
                  </li>
                );
              })}
            </ul>
          </Fold>
        );
      })}
    </div>
  );
}
